/**
 * Cuentas de precio de la pantalla de Planes (card 1053). Todo es a precio de LISTA:
 * el precio real lo confirma Ventas (465 cuentas tienen descuentos vigentes), así que
 * la pantalla nunca muestra lo que paga la cuenta, sólo la referencia.
 */

// Tramos de `plan_pricing` de menor a mayor `maxResources` (null = sin tope, va último).
const sortedTiers = (plan) =>
  [...(plan?.pricing || [])].sort((a, b) => {
    if (a.maxResources == null) return 1;
    if (b.maxResources == null) return -1;
    return a.maxResources - b.maxResources;
  });

// Precio por agenda con `count` agendas: el primer tramo que las cubre, como cobra el api
// (planCostCalculate: el tramo se elige por la cantidad total, no se escalona). Sin tramo
// que las cubra → null.
export function tierPrice(plan, count) {
  const tier = sortedTiers(plan).find((t) => t.maxResources == null || t.maxResources >= count);
  const price = tier?.pricePerResource;
  return Number.isFinite(price) ? price : null;
}

// Precio de lista por agenda = el tramo que cubre a 1 agenda. Sin tramos → null.
export function listPrice(plan) {
  const price = sortedTiers(plan)[0]?.pricePerResource;
  return Number.isFinite(price) ? price : null;
}

// Total de lista con las agendas activas de la cuenta, al precio de su tramo. null si no se
// conocen o falta el precio.
export function listTotal(plan, activeAgendas) {
  if (!Number.isInteger(activeAgendas) || activeAgendas <= 0) return null;
  const price = tierPrice(plan, activeAgendas);
  return price == null ? null : price * activeAgendas;
}

// Precio por agenda que muestra la card: el del tramo de las agendas activas si se conocen
// (como el total, así "$X / agenda" y "$T con tus N agendas" cierran); si no, o si ningún
// tramo las cubre, el de lista (1 agenda).
export function agendaPrice(plan, activeAgendas) {
  const known = Number.isInteger(activeAgendas) && activeAgendas > 0;
  return (known ? tierPrice(plan, activeAgendas) : null) ?? listPrice(plan);
}

// Diferencia mensual de pasar de `from` a `to`: por agenda (al tramo de las agendas activas,
// o de lista si no se conocen), y total con las agendas activas al tramo de cada plan si se
// conocen. null si falta algún precio.
export function upgradeDifference(from, to, activeAgendas) {
  const a = agendaPrice(from, activeAgendas);
  const b = agendaPrice(to, activeAgendas);
  if (a == null || b == null) return null;
  const fromTotal = listTotal(from, activeAgendas);
  const toTotal = listTotal(to, activeAgendas);
  return {
    perAgenda: b - a,
    total: fromTotal != null && toTotal != null ? toTotal - fromTotal : null,
  };
}

// Promo de Premium (api#588): "1/12" de `promoUntil` (YYYY-MM-DD, último día en que aplica).
// Se arma del texto: con new Date() la fecha sin hora se corre un día al pasarla a Argentina.
export function formatPromoUntil(until) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(until || "");
  return match ? `${Number(match[3])}/${Number(match[2])}` : null;
}

// Precio de lista por agenda que paga después de la promo (tramos de lista del catálogo), al
// tramo de las agendas activas si se conocen, como el resto de la card (FR-01 de QA, 04/11:
// con 5 agendas decía $45.999, el de 1 agenda, en vez de $36.799). Sin agendas, el de 1 agenda.
// Sólo si es mayor que el de la promo: mientras Comercial no cargue la lista son iguales → null.
export function priceAfterPromo(plan, activeAgendas) {
  const after = agendaPrice({ pricing: plan?.listPricing }, activeAgendas);
  const now = agendaPrice(plan, activeAgendas);
  return after != null && now != null && after > now ? after : null;
}

const formatter = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 });

// "$24.900": sin decimales y con punto de miles, como el diseño.
export function formatPrice(value) {
  return `$${formatter.format(value)}`;
}
